import type { PropsWithChildren } from "react";

import { useEffect, useState } from "react";
import { StreamChat } from "stream-chat";

import { env } from "@/env";
import { reportStreamChatError } from "@/lib/stream-chat-errors";

import { StreamContext } from "./stream-context";
import { useMe } from "./user-context";

export function StreamContextWrapper({ children }: PropsWithChildren) {
  const { streamUser } = useMe();

  // Club freemium : l'API ne délivre pas de token StreamChat. L'app tourne alors
  // sans client de chat (la page messagerie affiche l'argumentaire à la place).
  // Sans ce cas, un token null bloquait le rendu de toute l'application.
  if (!streamUser.token) {
    return <StreamContext.Provider value={null}>{children}</StreamContext.Provider>;
  }

  return (
    <StreamClientProvider token={streamUser.token} userId={streamUser.id}>
      {children}
    </StreamClientProvider>
  );
}

function StreamClientProvider({ token, userId, children }: PropsWithChildren<{ token: string; userId: string }>) {
  const [client, setClient] = useState<StreamChat | null>(null);
  const [connectionAttempt, setConnectionAttempt] = useState(0);

  useEffect(() => {
    const nextClient = new StreamChat(env.STREAMCHAT_API_KEY, {
      timeout: 10000,
    });
    let cancelled = false;
    let retryTimeout: number | undefined;

    void nextClient.connectUser({ id: userId }, token)
      .then(() => {
        if (!cancelled) {
          setClient(nextClient);
        }
      })
      .catch((error: unknown) => {
        reportStreamChatError(error, "connect-user");

        if (!cancelled) {
          const retryDelay = Math.min(5000 * 2 ** connectionAttempt, 30000);
          retryTimeout = window.setTimeout(() => {
            setConnectionAttempt(attempt => attempt + 1);
          }, retryDelay);
        }
      });

    return () => {
      cancelled = true;
      window.clearTimeout(retryTimeout);
      void nextClient.disconnectUser().catch((error: unknown) => {
        reportStreamChatError(error, "disconnect-user");
      });
    };
  }, [connectionAttempt, token, userId]);

  return <StreamContext.Provider value={client}>{children}</StreamContext.Provider>;
}
